import React, { useEffect, useState } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import {
  ChartPieIcon,
  ClockIcon,
  CpuChipIcon,
  ExclamationCircleIcon,
  TruckIcon,
  UsersIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../context/SocketContext';
import ToastAlerta from './common/ToastAlerta';
import NotificacionPanel from './common/NotificacionPanel';
import InstalarPWA from './common/InstalarPWA';
import BannerNotificaciones from './common/BannerNotificaciones';
import usePushNotifications from '../hooks/usePushNotifications';
import useInactividad from '../hooks/useInactividad';
import { inicializarAudio, reproducirAlerta } from '../utils/sonidoAlerta';
import { cn } from '../lib/utils';
import logo from '../assets/logo.jpeg';

const NAV = [
  { seccion: 'Principal', items: [
    { path: '/',            label: 'Dashboard',   Icon: ChartPieIcon },
    { path: '/incidentes',  label: 'Incidentes',  Icon: ExclamationCircleIcon },
    { path: '/despacho',    label: 'Despacho',    Icon: TruckIcon },
    { path: '/respuestas',  label: 'Respuestas',  Icon: ChatBubbleLeftRightIcon },
  ]},
  { seccion: 'Análisis', items: [
    { path: '/prediccion', label: 'Predicción IA', Icon: CpuChipIcon },
    { path: '/historial',  label: 'Historial',     Icon: ClockIcon },
  ]},
  { seccion: 'Administrar', items: [
    { path: '/usuarios', label: 'Usuarios', Icon: UsersIcon },
  ]},
];

const PAGE_TITLES = {
  '/':            { title: 'Dashboard',     sub: 'Operaciones en Tiempo Real' },
  '/incidentes':  { title: 'Incidentes',    sub: 'Gestión de incidentes' },
  '/despacho':    { title: 'Despacho',      sub: 'Control de unidades despachadas' },
  '/respuestas':  { title: 'Respuestas',    sub: 'Respuestas de bomberos por incidente' },
  '/prediccion':  { title: 'Predicción IA', sub: 'Análisis de riesgo con Machine Learning' },
  '/historial':   { title: 'Historial',     sub: 'Registro histórico de incidentes' },
  '/usuarios':    { title: 'Usuarios',      sub: 'Administración de usuarios' },
};

function iniciales(nombre = '') {
  const partes = nombre.split(/[.\s_-]/);
  return partes.slice(0, 2).map(p => p[0]?.toUpperCase() || '').join('') || '??';
}

export default function Layout() {
  const { sesion, logout, esAdmin, esBombero } = useAuth();
  const { noLeidas, notificaciones } = useSocket();
  const { cargando: pushCargando, activarPush } = usePushNotifications();

  // Auto-logout por inactividad — solo para bomberos (10 min)
  useInactividad({ activo: esBombero(), onInactividad: logout });
  const [bannerCerrado, setBannerCerrado] = React.useState(
    () => !!sessionStorage.getItem('push_banner_cerrado')
  );
  const location = useLocation();
  const [fechaHora,      setFechaHora]      = useState('');
  const [panelAbierto,   setPanelAbierto]   = useState(false);
  const [toast,          setToast]          = useState(null);
  const [sidebarAbierto, setSidebarAbierto] = useState(false);

  // Ref para alarma pendiente (cuando app abre desde notificación y AudioContext aún no está listo)
  const pendingAlarm = React.useRef(null);

  // Al montar: leer ?alarm= de la URL (puesto por el SW en notificationclick)
  useEffect(() => {
    if (!esBombero()) return;
    const params = new URLSearchParams(window.location.search);
    const alarm = params.get('alarm');
    if (alarm) {
      pendingAlarm.current = alarm;
      window.history.replaceState({}, '', window.location.pathname);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cada gesto del usuario: desbloquea AudioContext y toca alarma pendiente si hay
  useEffect(() => {
    const onGesture = () => {
      inicializarAudio();
      if (esBombero() && pendingAlarm.current) {
        reproducirAlerta(pendingAlarm.current, 25);
        pendingAlarm.current = null;
      }
    };
    window.addEventListener('click',      onGesture, { passive: true });
    window.addEventListener('touchstart', onGesture, { passive: true });
    return () => {
      window.removeEventListener('click',      onGesture);
      window.removeEventListener('touchstart', onGesture);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // BroadcastChannel (push mientras app abierta) + postMessage del SW — solo bomberos
  useEffect(() => {
    if (!esBombero()) return;

    const onAlarm = (e) => {
      if (e.data?.type === 'PIAPO_ALARM') {
        const prioridad = e.data.prioridad || 'alta';
        inicializarAudio();
        reproducirAlerta(prioridad, 25);
        // Guardar como pendiente por si el AudioContext estaba suspendido
        pendingAlarm.current = prioridad;
        setTimeout(() => { pendingAlarm.current = null; }, 30000);
      }
    };

    let ch = null;
    if ('BroadcastChannel' in window) {
      ch = new BroadcastChannel('piapo-alarm');
      ch.addEventListener('message', onAlarm);
    }
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', onAlarm);
    }

    return () => {
      if (ch) { ch.removeEventListener('message', onAlarm); ch.close(); }
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', onAlarm);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const prevCount = React.useRef(0);
  useEffect(() => {
    if (notificaciones.length > prevCount.current) setToast(notificaciones[0]);
    prevCount.current = notificaciones.length;
  }, [notificaciones]);

  useEffect(() => { setSidebarAbierto(false); }, [location.pathname]);

  useEffect(() => {
    const tick = () => {
      const now = new Date();
      setFechaHora(
        now.toLocaleDateString('es-GT', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
        + ' — ' + now.toLocaleTimeString('es-GT')
      );
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, []);

  const currentPage = PAGE_TITLES[location.pathname] || { title: 'PIAPO', sub: '' };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
        *, *::before, *::after { box-sizing: border-box; }
        body { font-family: 'Inter','Segoe UI',sans-serif; background: #f0f2f5; overflow-x: hidden; }
        @keyframes live-pulse { 0%,100%{opacity:1} 50%{opacity:.35} }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>

      <div className="flex min-h-screen overflow-x-hidden w-full">

        {/* OVERLAY MOBILE */}
        {sidebarAbierto && (
          <div
            className="fixed inset-0 bg-black/50 z-[199] md:hidden"
            onClick={() => setSidebarAbierto(false)}
          />
        )}

        {/* SIDEBAR */}
        <aside className={cn(
          'w-60 min-h-screen bg-[#1a1a2e] flex flex-col fixed top-0 left-0 z-[200] transition-transform duration-300',
          'md:translate-x-0',
          sidebarAbierto ? 'translate-x-0 shadow-2xl' : '-translate-x-full md:translate-x-0'
        )}>
          {/* Brand */}
          <div className="flex items-center gap-2.5 px-5 py-[18px] border-b border-white/[0.08]">
            <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0">
              <img src={logo} alt="CVB" className="w-full h-full object-cover" />
            </div>
            <div className="text-white font-extrabold text-[.95rem] leading-tight">
              PIAPO<br />
              <small className="text-white/45 font-normal text-[.7rem]">Cía. 44 Jalapa</small>
            </div>
          </div>

          {/* Nav */}
          <nav className="flex-1 py-2">
            {NAV.filter(grupo => {
              if (grupo.seccion === 'Administrar') return esAdmin();
              if (esBombero() && grupo.seccion === 'Análisis') return false;
              return true;
            }).map(grupo => (
              <React.Fragment key={grupo.seccion}>
                <div className="px-5 pt-4 pb-1.5 text-white/30 text-[.68rem] font-bold tracking-[1.5px] uppercase">
                  {grupo.seccion}
                </div>
                {grupo.items
                  .filter(item => {
                    if (esBombero()) return !(item.path === '/' || item.path === '/incidentes' || item.path === '/respuestas');
                    return item.path !== '/despacho';
                  })
                  .map(({ path, label, Icon }) => (
                    <Link
                      key={path}
                      to={path}
                      onClick={() => setSidebarAbierto(false)}
                      className={cn(
                        'flex items-center gap-3 px-5 py-[11px] text-[.88rem] no-underline',
                        'border-l-[3px] transition-all duration-150',
                        location.pathname === path
                          ? 'bg-white/[0.08] text-white font-semibold border-l-[#C0392B]'
                          : 'text-white/60 border-l-transparent hover:bg-white/[0.06] hover:text-white'
                      )}
                    >
                      <Icon className="h-[18px] w-[18px] flex-shrink-0" />
                      {label}
                    </Link>
                  ))}
              </React.Fragment>
            ))}
          </nav>

          {/* Footer */}
          <div className="mt-auto px-5 py-4 border-t border-white/[0.08]">
            <div className="flex items-center gap-2.5">
              <div className="h-[34px] w-[34px] bg-[#C0392B] rounded-full flex items-center justify-center text-white text-[.78rem] font-bold flex-shrink-0">
                {iniciales(sesion?.usuario)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-white text-[.82rem] font-semibold truncate">{sesion?.usuario || 'Usuario'}</div>
                <div className="text-white/40 text-[.7rem] truncate">
                  {sesion?.roles?.[0] || ''} · Cía {sesion?.cod_compania?.[0] || '—'}
                </div>
              </div>
              <button
                onClick={logout}
                title="Cerrar sesión"
                className="text-white/30 hover:text-white/80 transition-colors bg-transparent border-none cursor-pointer p-0 flex items-center"
              >
                <svg className="h-[18px] w-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h6a2 2 0 012 2v1"/>
                </svg>
              </button>
            </div>
          </div>
        </aside>

        {/* MAIN */}
        <div className="ml-0 md:ml-60 flex-1 flex flex-col min-h-screen">

          {/* BANNER NOTIFICACIONES PUSH */}
          {'Notification' in window &&
            Notification.permission !== 'granted' &&
            !bannerCerrado &&
            !sessionStorage.getItem('push_subscribed') && (
            <BannerNotificaciones
              onActivar={activarPush}
              cargando={pushCargando}
              onCerrar={() => {
                setBannerCerrado(true);
                sessionStorage.setItem('push_banner_cerrado', '1');
              }}
            />
          )}

          {/* TOPBAR */}
          <div className="bg-white px-4 md:px-6 py-3.5 flex items-center justify-between border-b border-[#e8eaf0] shadow-[0_1px_4px_rgba(0,0,0,.06)] sticky top-0 z-[100] gap-3">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              {/* Hamburger mobile */}
              <button
                onClick={() => setSidebarAbierto(!sidebarAbierto)}
                className="md:hidden bg-transparent border-none cursor-pointer p-2 text-gray-500 flex items-center"
                title="Menú"
              >
                <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 12h18M3 6h18M3 18h18" />
                </svg>
              </button>
              <div className="min-w-0">
                <div className="font-bold text-[1.05rem] text-gray-900 flex items-center gap-2">
                  <span
                    className="inline-block h-2 w-2 rounded-full bg-green-500"
                    style={{ animation: 'live-pulse 1.5s infinite' }}
                  />
                  {currentPage.title}
                </div>
                <div className="text-[.78rem] text-gray-400 truncate">
                  {location.pathname === '/' ? fechaHora : currentPage.sub}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2.5 flex-shrink-0">
              <button
                onClick={() => setPanelAbierto(p => !p)}
                title="Alertas"
                className="relative h-9 w-9 rounded-full bg-gray-100 border-none flex items-center justify-center cursor-pointer flex-shrink-0 hover:bg-gray-200 transition-colors"
              >
                <svg className="h-[18px] w-[18px] text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                  <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                </svg>
                {noLeidas > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-[#C0392B] rounded-full border-2 border-white text-white text-[.62rem] font-bold flex items-center justify-center px-0.5">
                    {noLeidas > 99 ? '99+' : noLeidas}
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* PÁGINA */}
          <div className="flex-1 overflow-y-auto p-4 md:p-5">
            <Outlet />
          </div>
        </div>
      </div>

      {toast && <ToastAlerta incidente={toast} onClose={() => setToast(null)} conSonido={esBombero()} />}
      {panelAbierto && <NotificacionPanel onClose={() => setPanelAbierto(false)} />}
      <InstalarPWA />
    </>
  );
}